import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import styles from "./LandingHero.module.scss";
import { getCDNImageUrl } from "@/utils/Common";
import LandingEnquiryForm from "./LandingEnquiryForm";
// Statically imported on purpose: this modal is a forwardRef component opened
// via ref, and next/dynamic does NOT forward refs (the ref would stay null and
// the modal would never open). It is still only rendered after a CTA click.
import ContactUsModalBtn from "@/components/Modal/ContactUs";

// The other two are controlled by an `open` prop, so they can be lazy-loaded:
// below-the-fold chrome that only mounts once a button is pressed (matches the
// repo CWV convention).
const SampleRequestModal = dynamic(
  () => import("@/components/Products/SampleRequestModal"),
  { ssr: false },
);
const ContactUsFormModal = dynamic(
  () => import("@/components/us-website/ContactUsFormModal/ContactUsFormModal"),
  { ssr: false },
);

/**
 * Landing hero. Two variants driven by config:
 *  - supplier: `showForm: true` → left copy + CTA, right enquiry form card.
 *  - product:  `image` set      → left copy + CTA, right product image.
 *
 * `highlightWord` (if present within `heading`) is rendered in the brand color.
 * The hero form is config-driven via `form` (see LandingEnquiryForm).
 *
 * Description clamping (Figma 5594-7335 / 5598-11242): the copy is clamped to
 * `descriptionClamp` LINES with an inline "read more" control that expands it in
 * place. Full text always ships in the HTML for SEO — the clamp only hides the
 * tail visually until expanded. The control is a TOGGLE: once expanded it reads
 * "read less" and collapses the paragraph again.
 *
 * CTAs are config-driven per breakpoint, because the two Figma frames genuinely
 * differ in WHICH buttons they show, not just how they lay out:
 *   `desktopCtas` — a row of outlined buttons under the copy (4672-17248).
 *   `mobileCtas`  — the same stacked full-width with trailing arrows, PLUS
 *                   "Request Sample" (5597-7704), which desktop omits because
 *                   the desktop hero already carries the sample-request form in
 *                   its right-hand card. Mobile hides that form, so there the
 *                   button is the only route to it.
 * Each list renders into its own breakpoint-scoped row. `desktopCtas` falls back
 * to `mobileCtas` (then to a single `ctaText` button) so a config that sets only
 * one of them still renders on both sizes.
 *
 * CTA behaviour: each button may carry an `action` that opens an existing shared
 * modal instead of navigating —
 *   "quote"       → ContactUsModalBtn      (Request Quote)
 *   "sample"      → SampleRequestModal     (Request Sample)
 *   "procurement" → ContactUsFormModal     (dark "Streamline …" contact form)
 * A button with no `action` just links to `href` (default `#lp-enquiry`).
 */
// Visible lines of hero description before "read more" (Figma shows 4 on
// desktop; the mobile breakpoint widens this in CSS).
const DEFAULT_CLAMP = 4;

/**
 * Trailing arrow for the mobile CTAs. Inlined (not an <img>) because each button
 * variant needs a different arrow colour and `currentColor` does not inherit
 * into an external SVG loaded via <img>.
 */
function ArrowRight() {
  return (
    <svg
      className={styles.mobileBtnIcon}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function LandingHero({
  heading = "",
  highlightWord,
  description,
  descriptionClamp = DEFAULT_CLAMP,
  readMoreText = "read more",
  readLessText = "read less",
  ctaText,
  ctaHref = "#lp-enquiry",
  ctaAction = "procurement",
  desktopCtas,
  mobileCtas,
  quoteHeading = "Request Quote",
  quoteSubHeading = "Fill in the details required to get your quote.",
  quoteMessage,
  showForm = false,
  form,
  image,
}) {
  const [expanded, setExpanded] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  // The quote modal is ref-controlled (no `open` prop), so it has to be mounted
  // before it can be opened. `quoteMounted` mounts it on first click and the
  // effect below opens it as soon as the ref is attached.
  const [quoteMounted, setQuoteMounted] = useState(false);
  const quoteModalRef = useRef(null);

  useEffect(() => {
    if (quoteMounted) quoteModalRef.current?.setOpen(true);
  }, [quoteMounted]);

  // Maps a CTA's `action` to its modal. Anything without a known action falls
  // through to plain `href` navigation, so config stays in control.
  const openAction = (action) => {
    if (action === "quote") {
      // Already mounted (reopening) → open directly; otherwise mount first.
      if (quoteMounted) quoteModalRef.current?.setOpen(true);
      else setQuoteMounted(true);
      return;
    }
    if (action === "sample") setSampleOpen(true);
    if (action === "procurement") setContactOpen(true);
  };

  const renderHeading = () => {
    if (!highlightWord || !heading.includes(highlightWord)) return heading;
    const [before, after] = heading.split(highlightWord);
    return (
      <>
        {before}
        <span className={styles.highlight}>{highlightWord}</span>
        {after}
      </>
    );
  };

  // Clamped visually via CSS line-clamp rather than by slicing the string, so
  // the entire paragraph stays in the crawlable HTML — important on these SEO
  // landing pages. `descriptionClamp` is a line count.
  const isClamped = !expanded && !!description;

  // Single-button fallback for configs that declare neither CTA list.
  const fallbackCtas = ctaText
    ? [{ text: ctaText, href: ctaHref, action: ctaAction, variant: "outline" }]
    : [];

  const mobileButtons =
    mobileCtas?.length > 0 ? mobileCtas : desktopCtas || fallbackCtas;
  const desktopButtons =
    desktopCtas?.length > 0 ? desktopCtas : mobileCtas || fallbackCtas;

  /**
   * Renders one CTA list. `showArrow` is mobile-only — Figma's desktop buttons
   * are text-only while every mobile button carries a trailing arrow.
   */
  const renderCtas = (buttons, rowClass, showArrow) => {
    if (!buttons.length) return null;
    return (
      <div className={rowClass}>
        {buttons.map((btn, i) => {
          const cls =
            btn.variant === "filled" || (i === 0 && !btn.variant)
              ? styles.ctaFilled
              : styles.ctaOutline;

          const content = (
            <>
              {btn.text}
              {showArrow && <ArrowRight />}
            </>
          );

          // An `action` opens a modal; otherwise fall back to a link.
          return btn.action ? (
            <button
              key={i}
              type="button"
              className={cls}
              onClick={() => openAction(btn.action)}
            >
              {content}
            </button>
          ) : (
            <a key={i} href={btn.href || ctaHref} className={cls}>
              {content}
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.copy}>
          <h1 className={styles.heading}>{renderHeading()}</h1>

          {description && (
            <div className={styles.descWrap}>
              <p
                className={`${styles.desc} ${isClamped ? styles.descClamped : ""}`}
                style={{ "--desc-lines": descriptionClamp }}
              >
                {description}
              </p>
              {/* Toggle, not a one-way expand: once expanded the same control
                  collapses the paragraph again. */}
              <button
                type="button"
                className={styles.readMore}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? readLessText : readMoreText}
              </button>
            </div>
          )}

          {renderCtas(desktopButtons, styles.ctaRowDesktop, false)}
          {renderCtas(mobileButtons, styles.ctaRowMobile, true)}
        </div>

        <div
          className={`${styles.aside} ${showForm ? styles.asideForm : ""}`}
          id="lp-enquiry"
        >
          {showForm ? (
            <LandingEnquiryForm form={form} />
          ) : image ? (
            <div className={styles.imageWrap}>
              <Image
                src={getCDNImageUrl(image)}
                alt={heading}
                fill
                priority
                sizes="(max-width: 900px) 100vw, 50vw"
                style={{ objectFit: "cover" }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* CTA modals. Only mounted once their action has been triggered, except
          the quote modal, which is ref-controlled and so must already exist. */}
      {quoteMounted && (
        <ContactUsModalBtn
          ref={quoteModalRef}
          heading={quoteHeading}
          subHeading={quoteSubHeading}
          service="1"
          additionalInformationInitialValue={quoteMessage}
          isUsVersion
        />
      )}

      {sampleOpen && (
        <SampleRequestModal
          open={sampleOpen}
          onClose={() => setSampleOpen(false)}
        />
      )}

      {contactOpen && (
        <ContactUsFormModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
        />
      )}
    </section>
  );
}
